/* eslint-disable arrow-body-style */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getLocale, shallowEqual, history, useLocation, useIntl, useSelector } from 'umi';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { getApiData } from '@/pages/dashboard/services/common';
import { commonValueFormatter, formatFilterValues, formatSmallValue } from "@/pages/dashboard/utils/utils";
import { PERIODTIMEOBJ } from '../../_config/constant';
import MetricsTrendCo from '.';
import './index.scss';
import moment from 'moment';
import { getEchartTooltipText } from '../EchartsBubble';
import { useLinkPath } from '../../utils/customHook';

const EventAnalysisTrendCo = (props: any) => {
  const { className = '', style = {}, config = {} } = props;
  const { api, eventApi, componentMetrics, haveMore = false } = config || {};
  const isEN = getLocale() === 'en-US';
  const location = useLocation();
  const { formatMessage } = useIntl();
  const linkPathArray = useLinkPath('eventAnalysis/eventDetails');
  const linkPath = Array.isArray(linkPathArray) ? linkPathArray[0] : linkPathArray;
  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const { timeType = 'Day', metrics = ['active_users', 'new_users'] } = filterVal || {};
  const { gameCode } = useSelector((state: any) => {
    return {
      gameCode: state?.dashboard?.currentGame?.game_code,
    };
  }, shallowEqual);

  const [metricData, setMetricData] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (filterLoading) return;
    getMetricData();
  }, [filterLoading, filterVal]);

  const getMetricData = () => {
    setLoading(true);
    const { timeType = 'Day', diff, rangeTime, metrics, event, selector, ...filters } = filterVal || {};
    const params: any = {
      game_code: gameCode,
      filters: formatFilterValues(filters),
      start_time: rangeTime?.[0],
      end_time: rangeTime?.[1],
      filter_event_date_field: 'start_time',
    };
    const metricsParams: any = {
      ...params,
      metrics,
    };
    const eventParams = {
      ...params,
      event_type_list: event?.eventType?.[0] === '255' ? [] : event?.eventType,
      event_name_list: event?.event?.[0] === '255' ? [] : event?.event,
      period: PERIODTIMEOBJ?.[timeType] || "daily",
    };
    // 特殊逻辑处理: 夜莺游戏日流失指标new_churn_users包含churn_level筛选器,跟产品确认先固定传255
    if (metrics?.includes('new_churn_users')) {
      metricsParams.filters = {
        ...formatFilterValues(filters),
        churn_level_list: ['255'],
      };
    }

    const getMetric = getApiData(api, metricsParams);
    const getEvent = getApiData(`${eventApi}?language=${isEN ? 'en' : 'cn'}`, eventParams);
    Promise.all([getEvent, getMetric]).then(([res1, res2]) => {
      setEventData(res1?.list || []);
      setMetricData(res2 || []);
      setLoading(false);
      setError(false);
    }).catch(() => {
      setMetricData([]);
      setEventData([]);
      setLoading(false);
      setError(true);
    });
  };

  const handleDate = (minDate: any, maxDate: any, date: any) => {
    const max = new Date(maxDate);
    const min = new Date(minDate);
    const our = new Date(date);
    if (our > max) return maxDate;
    if (our < min) return minDate;
    return date;
  };

  // 当事件的开始日期在日期筛选器筛选的开始日期之前时，只展示色块，不展示事件名称
  const isBeforeDate = (date: string, targetDate: string,) => {
    return moment(date).isSameOrAfter(targetDate, 'day');
  };

  const colorArr = ['#5086F3', '#77E097', '#6E7794', '#F8D74D', '#7F6FF1', '#5EC8F1', '#AD53D4', '#F19E55', '#488E89', '#F17C86'];

  const { eventList } = useMemo(() => {
    const list: any = [];
    const dateList: any[] = metricData?.map((item: any) => moment(item?.date)?.format('YYYY-MM-DD')) || [];
    // 取出接口数据所有事件
    eventData?.map((item: any) => {
      item?.event_list?.map((v: any) => {
        v?.event_record?.map((w: any) => {
          list?.push(w);
        });
      });
    });
    // 相同事件去重
    const setList = list.reduce((cur: any[], next: any) => {
      const hasDuplicate = cur.some((obj: any) => JSON.stringify(obj) === JSON.stringify(next));
      return hasDuplicate ? cur : [...cur, next];
    }, []) || [];
    // 处理事件数据
    const eventList = setList?.map((item: any, index: number) => {
      const { event_title_en, event_title_cn, start_time, end_time } = item || {};
      const backGroundStartTime = handleDate(dateList?.[0], dateList?.[dateList?.length - 1], start_time?.slice(0, 10));
      const backGroundEndTime = handleDate(dateList?.[0], dateList?.[dateList?.length - 1], end_time?.slice(0, 10));
      const isShowName = isBeforeDate(start_time, dateList?.[0]);
      const name = isEN ? (event_title_en || event_title_cn) : (event_title_cn || event_title_en);
      return {
        name,
        isShowName,
        startTime: moment(start_time)?.format('YYYYMMDD'),
        endTime: moment(end_time)?.format('YYYYMMDD'),
        backGroundStartTime: moment(backGroundStartTime)?.format('YYYYMMDD'),
        backGroundEndTime: moment(backGroundEndTime)?.format('YYYYMMDD'),
        isSingle: backGroundStartTime === backGroundEndTime,
        color: colorArr?.[index % 10]
      };
    });
    const eventListShow = eventList?.filter((item: { isShowName: any; })=>item?.isShowName) || [];
    // 处理文本展示换行所需要的startTimeIndex
    const startTimesCount: any = {};
    for (const item of eventListShow) {
      const time = item['backGroundStartTime'];
      if (time in startTimesCount) {
        startTimesCount[time] += 1;
      } else {
        startTimesCount[time] = 0;
      }
      item['startTimeIndex'] = startTimesCount[time];
    }
    return {
      eventList,
      dateList
    };
  }, [eventData, metricData, isEN]);

  const setOtherConfigFunc = (opt: any, config: any) => {
    const { isEvent } = config || {};
    if (opt?.series?.[0]?.type === 'bar') return opt;
    opt.tooltip.enterable = true;
    // opt.tooltip.backgroundColor = 'transparent';
    // opt.tooltip.extraCssText = 'box-shadow: none';
    const markArea: any = {
      data: [],
      label: {
        color: '#525464',
        offset: [3, 0],
        position: 'insideTopLeft',
        lineHeight: 20,
        formatter: (params: any) => {
          let pre = '';
          const isShowName = params?.data?.isShowName;
          Array(params?.data?.startTimeIndex)?.fill(0)?.map(() => pre = `${pre}\n`);
          return isShowName ? pre + params.data.name : '';
        }
      },
      emphasis: { disabled: true },
    };
    const markLine: any = {
      symbol: ['none', 'none'],
      animation: false,
      data: []
    };

    eventList?.map((item: any) => {
      const { name, startTimeIndex, backGroundStartTime, backGroundEndTime,isSingle, color, isShowName } = item || {};

      if ((isSingle || !isEvent)) {
        markLine?.data?.push({
          name,
          xAxis: backGroundStartTime,
          startTimeIndex,
          lineStyle: {
            color,
            type: 'solid',
            // 当事件背景收起的时候，如果不是单日事件，且当事件的开始日期在日期筛选器筛选的开始日期之前时，不展示竖线
            width: (!isShowName && !isEvent && !isSingle) ? 0 : 2,
            opacity: 0.5,
          },
          label: {
            show: isShowName,
            opacity: 1,
            color: '#525464',
            rotate: 0,
            distance: [8, -267 + startTimeIndex * 20],
            lineHeight: 20,
            position: 'insideStartBottom',
            formatter: (params: any) => {
              return  params.data.name || '';
            }
          },
        });
      } else {
        markArea?.data?.push([{
          name,
          isShowName,
          xAxis: backGroundStartTime,
          startTimeIndex,
          itemStyle: {
            color,
            opacity: 0.12,
          },
        },
        {
          xAxis: backGroundEndTime
        }]);
      }
    });
    if (opt?.series?.[0]) {
      opt.series[0] = {
        ...opt?.series?.[0],
        markLine,
        markArea,
      };
    }
    return opt;
  };

  const getEventsContent = (currentEvents: any) => {
    const data = currentEvents?.event_list || [];
    let str = '';
    data?.map((eventData: { event_type: any; event_record?: never[] | undefined; events: any; }) => {
      const { event_type, event_record = [] } = eventData;
      const eventTypeName = isEN ? event_type.name_en : (event_type.name_cn || event_type.name_en);
      let content = '';
      event_record?.map((record, index) => {
        const {event_title_en = '', event_title_cn } = record || {};
        const name = isEN ? (event_title_en || event_title_cn) : (event_title_cn || event_title_en);
        const color = eventList?.find((v: any) => v?.name === name)?.color;
        content += `<div class="popover-event-item" key="${index}">
         <div class="popover-event-item-block" style="background-color:${color}"></div>
            ${name}
          </div>`;
      });

      str += `<div class="event-type-details" key="${event_type.id}">
          <div class='popover-event-name'>${eventTypeName}</div>
        <div class='popover-event-desc'>
           ${content}
        </div>
      </div>`;
    });
    return str;
  };

  // 数据处，获取今天开始的事件和非今天开始的事件
  const filterEventRecords = (data) => {
    const { date, event_list } = data;
    const sameDateResult = {
      date: date,
      events: 0,
      event_list: []
    };
    const differentDateResult = {
      date: date,
      events: 0,
      event_list: []
    };

    event_list.forEach(event => {
      const { event_type, event_record } = event;
      const sameDateEvent = {
        event_type: event_type,
        event_record: [],
        events: 0
      };
      const differentDateEvent = {
        event_type: event_type,
        event_record: [],
        events: 0
      };

      event_record.forEach((record: { start_time: any; }) => {
        if (record.start_time === date) {
          sameDateEvent.event_record.push(record);
          sameDateEvent.events += 1;
        } else {
          differentDateEvent.event_record.push(record);
          differentDateEvent.events += 1;
        }
      });

      if (sameDateEvent.event_record.length > 0) {
        sameDateResult.event_list.push(sameDateEvent);
        sameDateResult.events += sameDateEvent.events;
      }

      if (differentDateEvent.event_record.length > 0) {
        differentDateResult.event_list.push(differentDateEvent);
        differentDateResult.events += differentDateEvent.events;
      }
    });

    return { sameDateResult, differentDateResult };
  };

  const formatterFunc = (params: any, metrics: any) => {
    const date = timeType === 'Month' ? moment(params?.[0]?.axisValue).format('YYYY-MM') : moment(params?.[0]?.axisValue).format('YYYY-MM-DD');
    const data = params?.map((item: { seriesName: any; value: number; color: any; }, index: number) => {
      const { metricKey, type, decimal } = metrics?.[index] || {};
      return {
        name: item.seriesName,
        metricsNum: metricKey === 'arpu' && item?.value < 1 && item?.value > 0 ? formatSmallValue(item?.value, 'decimals', decimal) : commonValueFormatter(type, item?.value, decimal),
        color: item.color
      };
    });
    const currentEvents = eventData[params?.[0]?.dataIndex];
    const { sameDateResult, differentDateResult } = filterEventRecords(currentEvents);
    if (haveMore) {
      const jumpToLink = () => {
        history.push({
          pathname: linkPath,
          query: {
            timeType: 'Day',
            date: currentEvents?.date || '',
            dashboardType: 'all',
            fromPage: location?.pathname
          }
        });
      };
      (window as any).jumpToLink = jumpToLink;
    }
    const extendNode = `
      <div class="popover-event events-popover">
      ${sameDateResult?.events > 0 ? `<div>
          <div class="title-flex">
            <div class="title-flex-text">
            <span class="events-text">${formatMessage({ id: "event_info_start_title" })} </span>
            <span class="events-year">(${sameDateResult?.events} ${formatMessage({ id: "event_info_events" })})</span>
            </div>
            ${haveMore ? `<div class="popover-event-more" onclick="jumpToLink()">${formatMessage({ id: 'event_more' })}</div>` : ''}
          </div>
          <div class="popover-event-body">
            ${getEventsContent(sameDateResult)}
          </div>
        </div>` : ''}
        ${differentDateResult?.events > 0 ? `<div class='events-popover-deferent'>
          <div class="title-flex">
          <div class="title-flex-text">
          <span class="events-text">${formatMessage({ id: "event_info_today_title" })} </span>
            <span class="events-year">(${differentDateResult?.events} ${formatMessage({ id: "event_info_events" })})</span>
          </div>
          ${haveMore ? `<div class="popover-event-more" onclick="jumpToLink()">${formatMessage({ id: 'event_more' })}</div>` : ''}
        </div>
        <div class="popover-event-body">
          ${getEventsContent(differentDateResult)}
        </div>
        </div>` : ''}
      </div>
      `;
    return getEchartTooltipText({
      className: `dbd-popover-tooltip-event popover-tooltip-event ${currentEvents?.events > 0 ? '' : 'popover-tooltip-event-visibile'}`,
      title: date,
      data,
      extendNode,
    });
  };

  const renderMetrics = useMemo(() => {
    // 配置跟筛选器key值过滤（排除key值配置错误）
    return componentMetrics?.filter((item: any) => metrics?.includes(item?.metricKey)) || [];
  }, [metrics, componentMetrics]);

  return <div className={className} style={style}>
    {
      renderMetrics?.map((selectedMetric: any) => {
        return <>
          <MetricsTrendCo
            config={{
              componentMetrics: [selectedMetric],
              titleEn: selectedMetric?.enName,
              titleCn: selectedMetric?.cnName,
              defaultAvtive: 3,
              setOtherConfigFunc,
              formatterFunc,
              showConfig: {
                showEvent: true,
                showEye: true,
                showBarChart: true,
                showLineChart: true,
                showTable: false,
                showDownloadTable: true,
              },
            }}
            metricData={metricData}
            loading={loading}
            error={error}
          />
        </>;
      })
    }
  </div>;
};


export default EventAnalysisTrendCo;